import { ArrowUpRight, CalendarClock, ScanSearch, ShieldCheck } from "lucide-react";
import { CollapsibleSection } from "@nexus/ui";
import {
  CampaignLiveDot as LiveDot,
  EventTypeIcon,
  formatDate,
  formatDateTime,
  isEventLive,
  PopularityMeter,
  type CampaignEngineSummary,
  type CampaignEventSummary,
  type CampaignSummary,
} from "@nexus/widget-kit";
import { DEFAULT_CAMPAIGN_LIST_LABELS, type CampaignListLabels } from "./labels";

function CountSummary({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex min-w-28 shrink-0 justify-end text-xs tabular-nums text-muted">
      {children}
    </span>
  );
}

function EngineChips({
  kind,
  title,
  notConfigured,
  engines,
  align,
}: {
  kind: "detection" | "response";
  title: string;
  notConfigured: string;
  engines: CampaignEngineSummary[];
  align: "left" | "right";
}) {
  const Icon = kind === "detection" ? ScanSearch : ShieldCheck;
  const tone = kind === "detection" ? "bg-data-detection/10" : "bg-data-response/10";
  const labelTone = kind === "detection" ? "text-data-detection" : "text-data-response";

  return (
    <div className={`min-w-0 flex-1 ${align === "right" ? "text-right" : ""}`}>
      <p
        className={`mb-1 inline-flex items-center gap-1 text-xs font-medium text-secondary ${
          align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        <Icon className={`size-3.5 ${labelTone}`} aria-hidden />
        {title}
      </p>
      <div className={`flex flex-wrap gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {engines.length === 0 ? (
          <span className="text-2xs text-muted">{notConfigured}</span>
        ) : (
          engines.map((engine) => (
            <span
              key={engine.engine_id}
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-medium text-secondary ${tone}`}
              title={engine.engine_group_label}
            >
              {engine.engine_label}
              {!engine.is_nagra && <span className="ml-1 text-muted">· third-party</span>}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function EventRow({
  event,
  nowMs,
  onSelect,
  onOpenEventMonitor,
  labels,
}: {
  event: CampaignEventSummary;
  nowMs: number;
  labels: CampaignListLabels;
  onSelect: (eventId: string, protectedEventId: string) => void;
  onOpenEventMonitor?: (eventId: string, protectedEventId: string) => void;
}) {
  const isLive = isEventLive(event.event_start, event.event_end, nowMs);

  return (
    <article className="rounded-md border border-border bg-surface px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <EventTypeIcon
            eventType={event.event_type_label}
            className="mt-0.5 h-5 w-5 shrink-0 text-brand-cyan"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h5 className="truncate text-sm font-semibold text-primary">{event.event_label}</h5>
              <PopularityMeter value={event.popularity} />
              {isLive && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-status-error-text">
                  <LiveDot /> {labels.live}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
              <span>{event.event_type_label}</span>
              {event.collection_group && <span>{event.collection_group}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-x-5 gap-y-3 pl-7 @3xl/widget:grid-cols-2 @5xl/widget:grid-cols-[minmax(10rem,0.7fr)_minmax(11rem,1fr)_minmax(11rem,1fr)_auto] @5xl/widget:items-start">
        <div>
          <p className="mb-1 text-xs font-medium text-secondary">{labels.schedule}</p>
          <p className="text-xs tabular-nums text-muted">
            {formatDateTime(event.event_start)} → {formatDateTime(event.event_end)}
          </p>
        </div>
        <EngineChips
          kind="detection"
          title={labels.detection}
          notConfigured={labels.notConfigured}
          engines={event.detection_engines} align="left" />
        <EngineChips
          kind="response"
          title={labels.response}
          notConfigured={labels.notConfigured}
          engines={event.response_engines} align="left" />
        <div className="flex flex-col items-start gap-1.5 @3xl/widget:col-span-2 @5xl/widget:col-span-1 @5xl/widget:items-end">
          <button
            type="button"
            onClick={() => onSelect(event.event_id, event.protected_event_id)}
            className="density-control-sm inline-flex items-center gap-1.5 rounded-lg bg-brand-cyan px-2.5 text-xs font-medium text-white transition-colors hover:bg-brand-cyan/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
          >
            <ShieldCheck className="size-3.5" aria-hidden />
            {labels.viewProtection}
          </button>
          {onOpenEventMonitor && (
            <button
              type="button"
              onClick={() => onOpenEventMonitor(event.event_id, event.protected_event_id)}
              className="inline-flex items-center gap-1 text-xs font-medium text-secondary underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
            >
              {labels.openMonitor}
              <ArrowUpRight className="size-3.5" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function CampaignAccordion({
  campaign,
  nowMs,
  open,
  onOpenChange,
  onSelectEvent,
  onOpenEventMonitor,
  labels,
}: {
  campaign: CampaignSummary;
  labels: CampaignListLabels;
  nowMs: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectEvent: (eventId: string, protectedEventId: string) => void;
  onOpenEventMonitor?: (eventId: string, protectedEventId: string) => void;
}) {
  return (
    <CollapsibleSection
      as="div"
      headingAs="div"
      open={open}
      onOpenChange={onOpenChange}
      className="rounded-md border-0"
      headerClassName="items-start border-b-0 bg-surface px-3 py-3 hover:bg-surface-elevated"
      leading={
        <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-elevated text-secondary">
          <CalendarClock className="size-4" aria-hidden />
        </span>
      }
      title={
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold text-primary">
              {campaign.campaign_label}
            </span>
            {campaign.has_off_streams && (
              <span
                className="inline-flex items-center rounded bg-status-warn-bg px-1.5 py-0.5 text-2xs font-medium text-status-warn-text"
                title="This campaign has off-stream detections"
              >
                {labels.offStream}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
            <span>
              <span className="font-medium text-secondary">{labels.starts}</span>{" "}
              {formatDate(campaign.campaign_start)}
            </span>
            <span>
              <span className="font-medium text-secondary">{labels.ends}</span>{" "}
              {formatDate(campaign.campaign_end)}
            </span>
            <span>
              Created {formatDate(campaign.created_at)} by {campaign.created_by}
            </span>
          </div>
        </div>
      }
      trailing={
        <CountSummary>
          {campaign.event_count} {campaign.event_count === 1 ? "event" : "events"} ·{" "}
          {campaign.engine_count} {campaign.engine_count === 1 ? "engine" : "engines"}
        </CountSummary>
      }
      contentClassName="space-y-2 bg-surface px-3 pb-3 pt-2 @2xl/widget:pl-9"
    >
      {campaign.events.length === 0 ? (
        <p className="p-3 text-xs text-muted">{labels.noEvents}</p>
      ) : (
        campaign.events.map((event) => (
          <EventRow
            key={event.event_id}
            event={event}
            nowMs={nowMs}
            onSelect={onSelectEvent}
            onOpenEventMonitor={onOpenEventMonitor}
            labels={labels}
          />
        ))
      )}
    </CollapsibleSection>
  );
}

export function CampaignList({
  campaigns,
  nowMs,
  expandedCampaignIds,
  onCampaignOpenChange,
  onSelectEvent,
  onOpenEventMonitor,
  labels = DEFAULT_CAMPAIGN_LIST_LABELS,
}: {
  labels?: CampaignListLabels;
  campaigns: CampaignSummary[];
  nowMs: number;
  expandedCampaignIds: string[];
  onCampaignOpenChange: (campaignId: string, open: boolean) => void;
  onSelectEvent: (eventId: string, protectedEventId: string) => void;
  onOpenEventMonitor?: (eventId: string, protectedEventId: string) => void;
}) {
  return (
    <div className="divide-y divide-border bg-surface">
      {campaigns.map((campaign) => (
        <CampaignAccordion
          key={campaign.campaign_id}
          campaign={campaign}
          nowMs={nowMs}
          open={expandedCampaignIds.includes(campaign.campaign_id)}
          onOpenChange={(open) => onCampaignOpenChange(campaign.campaign_id, open)}
          onSelectEvent={onSelectEvent}
          onOpenEventMonitor={onOpenEventMonitor}
          labels={labels}
        />
      ))}
    </div>
  );
}
